import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Flight,
  TrendingUp,
  Refresh,
  Speed,
  Height,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { liveFlightService, ProcessedFlight } from '../services/liveFlightService';
import { useAuth } from '../contexts/AuthContext';
import FirestoreService from '../services/firestoreService';

// Fix Leaflet default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Styled components
const StatsCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  transition: 'all 0.3s ease',
  background: theme.palette.mode === 'light'
    ? 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
    : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 8px 25px rgba(0, 0, 0, 0.1)'
      : '0 8px 25px rgba(0, 0, 0, 0.3)',
  },
}));

const ControlPanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  background: theme.palette.mode === 'light'
    ? 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
    : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
  border: `1px solid ${theme.palette.divider}`,
}));

const MapContainer_Styled = styled(Box)(({ theme }) => ({
  height: 600,
  borderRadius: 16,
  overflow: 'hidden',
  border: `2px solid ${theme.palette.divider}`,
  position: 'relative',
  '& .leaflet-container': {
    borderRadius: 16,
  },
  // Ensure polylines are properly rendered
  '& .leaflet-overlay-pane svg': {
    pointerEvents: 'auto',
  },
  '& .leaflet-interactive': {
    cursor: 'pointer',
  },
}));

// Utility function to get airline colors
const getAirlineColor = (airline: string): string => {
  const colors = [
    '#FF4444', '#00BFFF', '#32CD32', '#FF8C00', '#9370DB',
    '#FF69B4', '#00CED1', '#FFD700', '#FF6347', '#40E0D0',
    '#FFA500', '#98FB98', '#F0E68C', '#DDA0DD', '#87CEEB'
  ];
  
  let hash = 0;
  for (let i = 0; i < airline.length; i++) {
    hash = airline.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Flight markers component
const FlightMarkers: React.FC<{ 
  flights: ProcessedFlight[], 
  showOnGround: boolean,
  onFlightClick: (flight: ProcessedFlight) => void
}> = ({ flights, showOnGround, onFlightClick }) => {
  // Debug logging
  console.log('🛩️ Rendering flights:', flights.length);
  console.log('🛣️ Flights with paths:', flights.filter(f => f.path && f.path.length > 1).length);

  return (
    <>
      {flights.map((flight) => {
        // Skip ground aircraft if not showing them
        if (!showOnGround && flight.isOnGround) return null;
        
        console.log(`✈️ Flight ${flight.callsign}: Path length = ${flight.path?.length || 0}`);
        
        return (
          <React.Fragment key={flight.id}>
            {/* Flight Path - Enhanced for better visibility */}
            {flight.path && flight.path.length > 1 && (
              <>
                {/* Background stroke for better visibility */}
                <Polyline
                  positions={flight.path}
                  pathOptions={{
                    color: '#ffffff',
                    weight: 6,
                    opacity: 0.8,
                  }}
                />
                {/* Main flight path */}
                <Polyline
                  positions={flight.path}
                  pathOptions={{
                    color: getAirlineColor(flight.airline),
                    weight: 4,
                    opacity: 1.0,
                    dashArray: flight.isOnGround ? '10, 5' : undefined,
                  }}
                />
              </>
            )}
            
            {/* Aircraft Marker */}
            <Marker
              position={[flight.latitude, flight.longitude]}
              eventHandlers={{
                click: () => onFlightClick(flight),
              }}
              icon={L.divIcon({
                html: `
                  <div style="
                    transform: rotate(${flight.heading || 0}deg);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 30px;
                    height: 30px;
                    cursor: pointer;
                  ">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="${getAirlineColor(flight.airline)}" style="
                      filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.7));
                      stroke: #ffffff;
                      stroke-width: 0.5;
                    ">
                      <path d="M21,16V14L13,9V3.5A1.5,1.5 0 0,0 11.5,2A1.5,1.5 0 0,0 10,3.5V9L2,14V16L10,13.5V19L8,20.5V22L11.5,21L15,22V20.5L13,19V13.5L21,16Z"/>
                    </svg>
                  </div>
                `,
                className: 'flight-marker',
                iconSize: [30, 30],
                iconAnchor: [15, 15],
              })}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};

export const FlightMap: React.FC = () => {
  const { user } = useAuth();
  const [flights, setFlights] = useState<ProcessedFlight[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOnGround, setShowOnGround] = useState(false);
  const [selectedAirline, setSelectedAirline] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [selectedFlight, setSelectedFlight] = useState<ProcessedFlight | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterUserAirlines, setFilterUserAirlines] = useState(false);
  const [userAirlines, setUserAirlines] = useState<string[]>([]);

  // Load initial flight data
  useEffect(() => {
    loadFlights();
    loadUserAirlines();
    
    // Add test data with longer paths for demonstration
    const testFlights: ProcessedFlight[] = [
      {
        id: 'demo-path-1',
        callsign: 'AI101',
        latitude: 28.5,
        longitude: 77.2,
        altitude: 35000,
        speed: 480,
        heading: 90,
        airline: 'Air India',
        country: 'India',
        isOnGround: false,
        lastSeen: new Date(),
        origin: 'DEL (Delhi)',
        destination: 'BOM (Mumbai)',
        flightType: 'Domestic',
        path: [
          [28.5561, 77.1003], // Delhi
          [28.4, 76.8],
          [28.2, 76.5],
          [27.8, 76.0],
          [27.4, 75.5],
          [27.0, 75.0],
          [26.5, 74.5],
          [26.0, 74.0],
          [25.5, 73.5],
          [25.0, 73.0],
          [24.5, 72.5],
          [24.0, 72.0],
          [23.5, 71.5],
          [23.0, 71.0],
          [22.5, 70.5],
          [22.0, 70.0],
          [21.5, 69.5],
          [21.0, 69.0],
          [20.5, 68.5],
          [19.0996, 72.8656] // Mumbai
        ]
      },
      {
        id: 'demo-path-2',
        callsign: 'SG502',
        latitude: 19.1,
        longitude: 72.9,
        altitude: 37000,
        speed: 520,
        heading: 180,
        airline: 'SpiceJet',
        country: 'India',
        isOnGround: false,
        lastSeen: new Date(),
        origin: 'BOM (Mumbai)',
        destination: 'BLR (Bangalore)',
        flightType: 'Domestic',
        path: [
          [19.0996, 72.8656], // Mumbai
          [18.8, 73.2],
          [18.5, 73.6],
          [18.2, 74.0],
          [17.9, 74.4],
          [17.6, 74.8],
          [17.3, 75.2],
          [17.0, 75.6],
          [16.7, 76.0],
          [16.4, 76.4],
          [16.1, 76.8],
          [15.8, 77.2],
          [15.5, 77.6],
          [15.2, 77.8],
          [14.9, 77.9],
          [14.6, 78.0],
          [14.3, 78.1],
          [14.0, 78.2],
          [13.7, 78.1],
          [13.4, 77.9],
          [13.1979, 77.7060] // Bangalore
        ]
      },
      {
        id: 'demo-path-3',
        callsign: 'EK506',
        latitude: 25.2532,
        longitude: 55.3657,
        altitude: 39000,
        speed: 550,
        heading: 270,
        airline: 'Emirates',
        country: 'United Arab Emirates',
        isOnGround: false,
        lastSeen: new Date(),
        origin: 'DXB (Dubai)',
        destination: 'DEL (Delhi)',
        flightType: 'International',
        path: [
          [25.2532, 55.3657], // Dubai
          [25.1, 56.0],
          [24.8, 57.2],
          [24.5, 58.5],
          [24.2, 59.8],
          [23.9, 61.0],
          [23.6, 62.3],
          [23.3, 63.5],
          [23.0, 64.8],
          [22.7, 66.0],
          [22.4, 67.3],
          [22.1, 68.5],
          [21.8, 69.8],
          [21.5, 71.0],
          [21.2, 72.3],
          [20.9, 73.5],
          [20.6, 74.8],
          [20.3, 76.0],
          [20.0, 76.8],
          [19.5, 77.0],
          [28.5561, 77.1003] // Delhi
        ]
      },
      {
        id: 'demo-path-4',
        callsign: '6E2142',
        latitude: 22.7,
        longitude: 75.8,
        altitude: 33000,
        speed: 460,
        heading: 45,
        airline: 'IndiGo',
        country: 'India',
        isOnGround: false,
        lastSeen: new Date(),
        origin: 'BOM (Mumbai)',
        destination: 'CCU (Kolkata)',
        flightType: 'Domestic',
        path: [
          [19.0996, 72.8656], // Mumbai
          [19.5, 73.5],
          [20.0, 74.2],
          [20.5, 74.9],
          [21.0, 75.6],
          [21.5, 76.3],
          [22.0, 77.0],
          [22.5, 77.7],
          [23.0, 78.4],
          [23.5, 79.1],
          [24.0, 79.8],
          [24.5, 80.5],
          [25.0, 81.2],
          [25.5, 81.9],
          [26.0, 82.6],
          [26.5, 83.3],
          [27.0, 84.0],
          [27.5, 85.0],
          [28.0, 86.0],
          [22.5726, 88.3639] // Kolkata
        ]
      }
    ];

    // Add demo flights to the actual flights for better visualization
    setFlights(prev => {
      const demoIds = new Set(testFlights.map(f => f.id));
      const existingFlights = prev.filter(f => !demoIds.has(f.id));
      return [...existingFlights, ...testFlights];
    });
  }, []);

  const loadUserAirlines = async () => {
    if (!user?.id) return;
    
    try {
      const airlinePrograms = await FirestoreService.getUserLoyaltyProgramsByType(user.id, 'airline');
      console.log('🔍 Original airline programs:', airlinePrograms.map(p => p.name));
      
      const processedAirlines = airlinePrograms.map(program => 
        program.name.replace(/\s+(Airlines?|Airways?)/i, '').trim()
      );
      console.log('🔍 Processed airline names:', processedAirlines);
      
      const airlines = Array.from(new Set(processedAirlines)); // Remove duplicates with Set
      console.log('🎯 User airlines loaded (deduplicated):', airlines);
      
      setUserAirlines(airlines);
    } catch (error) {
      console.error('Error loading user airlines:', error);
    }
  };

  const loadFlights = async () => {
    setLoading(true);
    try {
      const flightData = await liveFlightService.getAllFlights();
      
      // Debug: Show sample flight data before filtering
      const sampleFlights = flightData.slice(0, 5);
      console.log('🛩️ Sample raw flight data before filtering:', sampleFlights.map(f => ({
        callsign: f.callsign,
        airline: f.airline,
        country: f.country
      })));
      
      setFlights(flightData);
      setStats(liveFlightService.getFlightStats(flightData));
    } catch (error) {
      console.error('Error loading flights:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshFlights = async () => {
    setRefreshing(true);
    try {
      const flightData = await liveFlightService.getAllFlights();
      setFlights(flightData);
      setStats(liveFlightService.getFlightStats(flightData));
    } catch (error) {
      console.error('Error refreshing flights:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const filteredFlights = flights.filter(flight => {
    // Filter by selected airline
    if (selectedAirline !== 'all' && flight.airline !== selectedAirline) {
      return false;
    }
    
    // Filter by user's airlines if enabled
    if (filterUserAirlines && userAirlines.length > 0) {
      const hasUserAirline = userAirlines.some(userAirline => 
        flight.airline.toLowerCase().includes(userAirline.toLowerCase()) ||
        userAirline.toLowerCase().includes(flight.airline.toLowerCase())
      );
      
      // Debug logging for airline filtering
      if (flight.callsign && (flight.callsign.includes('UAL') || flight.callsign.includes('UA') || flight.airline === 'United')) {
        console.log(`🔍 United flight found: ${flight.callsign}`);
        console.log(`   Original airline: "${flight.airline}"`);
        console.log(`   Country: "${flight.country}"`);
        console.log(`   Route: ${flight.origin} → ${flight.destination}`);
        console.log(`   User airlines: [${userAirlines.join(', ')}]`);
        console.log(`   Match found: ${hasUserAirline}`);
        console.log(`   Flight will be ${hasUserAirline ? 'SHOWN' : 'HIDDEN'}`);
      }
      
      if (!hasUserAirline) {
        return false;
      }
    }
    
    return showOnGround || !flight.isOnGround;
  });

  const uniqueAirlines = Array.from(new Set(flights.map(f => f.airline))).sort();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
          Live Flight Map
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
          Real-time flight tracking with points earning opportunities
        </Typography>

        {/* Stats Cards */}
        {stats && (
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 3, 
            mb: 3,
            '& > *': { 
              flex: '1 1 300px',
              minWidth: '250px'
            }
          }}>
            <StatsCard>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Flight color="primary" />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {stats.total.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Total Flights
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StatsCard>
            
            <StatsCard>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Height color="success" />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {stats.airborne.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Airborne
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StatsCard>

            <StatsCard>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Speed color="info" />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {stats.averageSpeed}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Avg Speed (kts)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StatsCard>

            <StatsCard>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp color="warning" />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {(stats.averageAltitude / 1000).toFixed(0)}K
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Avg Altitude (ft)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StatsCard>
          </Box>
        )}

        {/* Controls */}
        <ControlPanel>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 2, 
            alignItems: 'center',
            '& > *': { 
              flex: '1 1 250px',
              minWidth: '200px'
            }
          }}>
            <TextField
              select
              size="small"
              label="Filter by Airline"
              value={selectedAirline}
              onChange={(e) => setSelectedAirline(e.target.value)}
              sx={{ maxWidth: 300 }}
            >
              <MenuItem value="all">All Airlines</MenuItem>
              {uniqueAirlines.map((airline) => (
                <MenuItem key={airline} value={airline}>
                  {airline}
                </MenuItem>
              ))}
            </TextField>
            
            <FormControlLabel
              control={
                <Switch
                  checked={showOnGround}
                  onChange={(e) => setShowOnGround(e.target.checked)}
                />
              }
              label="Show Ground Aircraft"
            />

            {userAirlines.length > 0 && (
              <FormControlLabel
                control={
                  <Switch
                    checked={filterUserAirlines}
                    onChange={(e) => setFilterUserAirlines(e.target.checked)}
                    color="primary"
                  />
                }
                label={`My Airlines Only (${userAirlines.length})`}
              />
            )}

            <Button
              variant="outlined"
              onClick={refreshFlights}
              disabled={refreshing}
              startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
              sx={{ maxWidth: 200 }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </Button>

            <Chip 
              label={`${filteredFlights.length} flights shown`}
              color="primary"
              variant="outlined"
            />
          </Box>
        </ControlPanel>
      </Box>

      {filterUserAirlines && userAirlines.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            🎯 <strong>Filtering by your airlines:</strong> {userAirlines.join(', ')}. 
            {filteredFlights.length === 0 ? ' No flights currently visible from your airlines.' : ` Showing ${filteredFlights.length} flights.`}
          </Typography>
        </Alert>
      )}

      <MapContainer_Styled>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            flexDirection: 'column',
            gap: 2
          }}>
            <CircularProgress size={50} />
            <Typography variant="h6">Loading live flight data...</Typography>
          </Box>
        ) : (
          <MapContainer
            center={[20, 77]}
            zoom={5}
            style={{ height: '100%', width: '100%' }}
            zoomControl={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FlightMarkers 
              flights={filteredFlights} 
              showOnGround={showOnGround}
              onFlightClick={(flight) => {
                setSelectedFlight(flight);
                setDialogOpen(true);
              }}
            />
          </MapContainer>
        )}
      </MapContainer_Styled>

      {/* Top Airlines */}
      {stats && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Top Airlines (Live Flights)
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 2,
            '& > *': { 
              flex: '1 1 200px',
              minWidth: '180px'
            }
          }}>
            {stats.topAirlines.map((airline: any, index: number) => (
              <StatsCard key={airline.airline}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: getAirlineColor(airline.airline),
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {airline.airline}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {airline.count}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </StatsCard>
            ))}
          </Box>
        </Box>
      )}

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          📡 Live flight data provided by OpenSky Network. Click on any flight for detailed information!
        </Typography>
      </Alert>

      {/* Flight Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: selectedFlight ? getAirlineColor(selectedFlight.airline) : 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Flight />
          Flight Details - {selectedFlight?.callsign || 'Unknown'}
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          {selectedFlight && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Route Information */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  🛫 Route Information
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  p: 2,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                  borderRadius: 2,
                  border: (theme) => `1px solid ${theme.palette.divider}`
                }}>
                  <Box sx={{ textAlign: 'center', flex: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      DEPARTURE
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {selectedFlight.origin || 'Unknown'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 30, height: 2, bgcolor: getAirlineColor(selectedFlight.airline) }} />
                    <Flight sx={{ color: getAirlineColor(selectedFlight.airline) }} />
                    <Box sx={{ width: 30, height: 2, bgcolor: getAirlineColor(selectedFlight.airline) }} />
                  </Box>
                  
                  <Box sx={{ textAlign: 'center', flex: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      ARRIVAL
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {selectedFlight.destination || 'Unknown'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider />

              {/* Flight Details */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  ✈️ Flight Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Airline
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedFlight.airline}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Country
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedFlight.country}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Altitude
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Height fontSize="small" />
                      {selectedFlight.altitude ? `${selectedFlight.altitude.toLocaleString()} ft` : 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Speed
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Speed fontSize="small" />
                      {selectedFlight.speed ? `${selectedFlight.speed} kts` : 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Position
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedFlight.latitude.toFixed(4)}°, {selectedFlight.longitude.toFixed(4)}°
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedFlight.isOnGround ? 'On Ground' : 'Airborne'}
                        color={selectedFlight.isOnGround ? 'default' : 'success'}
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Flight Type
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedFlight.flightType || 'Unknown'}
                        color={selectedFlight.flightType === 'Domestic' ? 'primary' : 'secondary'}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              {selectedFlight.path && selectedFlight.path.length > 1 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      🛤️ Flight Path
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      This flight has been tracked through {selectedFlight.path.length} position updates, 
                      showing its recent trajectory on the map.
                    </Typography>
                  </Box>
                </>
              )}

              <Divider />

              {/* Points Earning Opportunity */}
              <Box sx={{ 
                p: 2,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'primary.900' : 'primary.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'primary.700' : 'primary.200'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  💳 Points Earning Opportunity
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedFlight.flightType === 'Domestic' 
                    ? `Flying domestic with ${selectedFlight.airline}? Earn extra miles and enjoy priority benefits with a co-branded credit card!`
                    : `Flying international with ${selectedFlight.airline}? Maximize your earning potential with airline credit cards and elite status!`
                  } Check our Cards section for the best options.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
